import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { OrganisationInterface } from 'src/app/model/organisations/organisation-interface';
import { ProviderInterface } from 'src/app/model/user/provider-interface';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { DialogService } from 'src/app/services/dialog-service/dialog-service.service';
import { HeaderServiceService } from 'src/app/services/header-service/header-service.service';
import { UserService } from 'src/app/services/user-service/userservice';
import { UtilService } from 'src/app/services/util-service/utilService';
import { API_BASE_FILESERVICE, API_MAP } from 'src/environments/api';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { OrganisationType } from 'src/app/model/enums/organisationType';
import { PackageModel } from 'src/app/model/stripe-models/product/package-model';
import { StripeService } from 'src/app/services/stripe-service/stripe.service';
import { UserLocalSelectionService } from 'src/app/services/dataServices/user-local-selection-service/user-local-selection.service';
import { CustomTextErrorStateMatcher, VALIDATORS, streetValidator } from "src/environments/validators";
import { MinLengthErrorStateMatcher } from 'src/environments/validators';
import { Subscription } from 'rxjs';
import { HOUSE_NUMBER_REGEX, PHONE_NUMBER_REGEX, CITY_REGEX, POSTAL_CODE_REGEX } from 'src/environments/constants';

@Component({
    selector: 'app-organisation-edit-settings',
    templateUrl: './organisation-edit-settings.component.html',
    styleUrls: ['./organisation-edit-settings.component.scss'],
    standalone: false
})
export class OrganisationEditSettingsComponent implements OnInit {
    @Input() organisation: OrganisationInterface;

    @Output() close = new EventEmitter<boolean>();

    package: PackageModel;
    organisationId: number;
    isLoading = false;

    organisationEditForm: UntypedFormGroup;
    get domain(): AbstractControl {
        return this.organisationEditForm.get('domain');
    }
    get phoneNumber(): AbstractControl {
        return this.organisationEditForm.get('phoneNumber');
    }
    get name(): AbstractControl {
        return this.organisationEditForm.get('name');
    }
    // get type(): AbstractControl {
    //     return this.organisationEditForm.get('type');
    // }
    get street(): AbstractControl {
        return this.organisationEditForm.get('street');
    }
    get hNr(): AbstractControl {
        return this.organisationEditForm.get('hNr');
    }
    get postalCode(): AbstractControl {
        return this.organisationEditForm.get('postalCode');
    }
    get city(): AbstractControl {
        return this.organisationEditForm.get('city');
    }
    get description(): AbstractControl {
        return this.organisationEditForm.get('description');
    }

    get contactGroup(): UntypedFormGroup {
        return this.organisationEditForm.get('contact') as UntypedFormGroup;
    }

    get url() {
        return this.organisationEditForm.get('url');
    }

    get contactName(): AbstractControl {
        return this.contactGroup.get('contactName');
    }

    get contactEmail(): AbstractControl {
        return this.contactGroup.get('email');
    }

    // readonly domainPrefix = '@';
    isDescriptionValid: boolean = true;
    isNewOrganisation: boolean = true;

    orgaNameErrorStateMatcher = new MinLengthErrorStateMatcher(160);
    private createContactSub: Subscription;

    constructor(
        private dialogService: DialogService,
        private userService: UserService,
        public utilService: UtilService,
        private organisationService: OrganisationService,
        private headerService: HeaderServiceService,
        private formBuilder: UntypedFormBuilder,
        public router: Router,
        private stripeService: StripeService,
        private localSelectionService: UserLocalSelectionService,
    ) { }

    @HostListener('window:beforeunload')
    onBeforeUnload() {
        // Delets Data if tab was closed
        this.localSelectionService.clearSelection('selectedPackage-register');
    }

    ngOnInit(): void {
        if (!this.organisation) {
            this.organisation = this.setEmptyOrganisation();
        }
        if (this.organisation.organisationId != null) {
            this.isNewOrganisation = false;
        }
        this.initializeForm(this.organisation);
        this.setupContactToggle();
        this.getPackage();
    }

    private initializeForm(organisation: OrganisationInterface) {
        this.organisationEditForm = this.formBuilder.group({
            name: [organisation.organisationName, [Validators.required, Validators.maxLength(160), Validators.minLength(2)]],
            // type: [organisation.organisationType, Validators.required],
            street: [organisation.address.street, streetValidator],
            hNr: [organisation.address.streetAdditional, [ Validators.required, Validators.pattern(HOUSE_NUMBER_REGEX), Validators.maxLength(15)]],
            postalCode: [organisation.address.postalCode, [Validators.required, Validators.pattern(POSTAL_CODE_REGEX)]],
            city: [organisation.address.city, [Validators.required, Validators.pattern(CITY_REGEX)]],
            domain: [organisation.domain, Validators.required],
            description: [organisation.description, [Validators.maxLength(500)]],
            url: [organisation.website, VALIDATORS.website],
            phoneNumber: [organisation.phoneNumber, Validators.pattern(PHONE_NUMBER_REGEX)],
            createContact: [false], // Toggle for contact creation
            contact: this.formBuilder.group({
                contactName: [''],
                phoneNumber: [''],
                mobilePhoneNumber: [''],
                email: [''],
                website: [''],
                gender: ['']
            })
        });
    }

    changeDomainToName(organisationName: string): void {
        let domainString: string = '';
        let format = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?äÄöÖüÜ]+/;
        for (let letter of organisationName) {
            if (format.test(letter)) {
                domainString = domainString.concat('_');
            } else {
                domainString = domainString.concat(letter);
            }
        }
        const domainField = this.domain;
        domainField.setValue(domainString);
        domainField.setErrors(null);
    }

    isUserPlatformAdmin(): boolean {
        return this.userService.isCurrentUserPlatformAdmin();
    }

    onDescriptionValidityChange(descriptionValid: boolean): void {
        this.isDescriptionValid = descriptionValid;
    }

    private getFormFieldValue(fieldName: string): any {
        return this.organisationEditForm.get(fieldName).value;
    }

    onOrganisationConfirm(): void {
        this.isLoading = true;
        const organisationName = this.getFormFieldValue('name');
        if (
            !UtilService.areStringsValid(
                organisationName,
                this.organisation.description
            )
        ) {
            this.isLoading = false;
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte stellen Sie sicher, dass der Name und die Beschreibung der Organisation nicht zu lang sind.'
            );
            return;
        }

        if (this.organisation.fileId == null) {
            this.isLoading = false;
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte wählen Sie ein Logo für die Organisation.'
            );
            return;
        }
        if (!this.checkPhonenumber()) {
            this.isLoading = false;
            return;
        }
        this.isLoading = false;
        this.dialogService
            .openConfirmDialog(
                'Möchtest du diese Organisation wirklich anlegen?',
                'Nein',
                'Ja',
                ''
            )
            .subscribe((result) => {
                if (result) {
                    this.isLoading = true;
                    this.applyChangesToOrganisation();
                    const contactData = this.organisationEditForm.get('createContact').value
                        ? this.organisationEditForm.get('contact').value
                        : null;

                    this.organisationService.postItem(this.organisation).then(
                        async (org) => {
                            this.organisationId = org.organisationId;

                            if (contactData) {
                                try {
                                    await this.organisationService
                                        .createContactForNewOrganisation(org.organisationId, contactData)
                                        .toPromise();
                                } catch (err) {
                                    console.warn('Kontakt konnte nicht angelegt werden', err);
                                    this.dialogService.openDialog('Hinweis', 'Kontakt konnte nicht angelegt werden.');
                                }
                            }

                            await this.organisationService.loadOrganisationsForUser();
                            this.isLoading = false;
                            this.setActiveProvider(org);

                            this.dialogService.openDialog(
                                'Organisation erstellt',
                                `Die neue Organisation ${org.organisationName} wurde erfolgreich erstellt. Sie werden jetzt weitergeleitet zum Zahlungsprozess.`
                            ).subscribe(() => this.payPackage());
                        },
                        (error) => {
                            this.isLoading = false;
                            this.dialogService.openDialog(
                                'Fehler beim Erstellen',
                                'Die Organisation konnte nicht erstellt werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.'
                            );
                        }
                    );
                    this.userService.updateAuthorisation();
                }
            });
    }

    private applyChangesToOrganisation(): void {
        this.organisation.organisationName = this.getFormFieldValue('name');
        const organisationTypes = this.organisation.organisationType ?? [];
        if (!organisationTypes.includes(OrganisationType.PRODUCT_PROVIDER)) {
            organisationTypes.push(OrganisationType.PRODUCT_PROVIDER);
        }
        this.organisation.organisationType = organisationTypes;
        this.organisation.address = {
            street: this.getFormFieldValue('street'),
            streetAdditional: this.getFormFieldValue('hNr'),
            postalCode: this.getFormFieldValue('postalCode'),
            city: this.getFormFieldValue('city')
        };
        this.organisation.contacts = [];
        this.organisation.domain = this.getFormFieldValue('domain');
        this.organisation.website = this.getFormFieldValue('url');
        this.organisation.phoneNumber = this.getFormFieldValue('phoneNumber');
        this.organisation.description = this.getFormFieldValue('description');
        if (
            this.utilService.isStringNullOrEmpty(this.organisation.description)
        ) {
            this.organisation.description = '';
        }
    }

    onAbort(): void {
        if (this.isNewOrganisation) {
            this.organisationEditForm.reset();
        }
        this.close.emit(false);
        this.router.navigate(['marktplatz']);
    }

    setEmptyOrganisation() {
        return {
            organisationId: null,
            organisationName: '',
            organisationType: [],
            address: {
                street: '',
                streetAdditional: '',
                postalCode: '',
                city: ''
            },
            contacts: [],
            contactPersons: [],
            offices: [],
            domain: '',
            website: '',
            description: '',
            fileId: null,
            organisationMembers: [],
            contact: '',
            distribution: '',
            membershipStatus: '',
            name: '',
            userId: null,
            role: '',
            organisationPicture: '',
            pictureId: null
        };
    }

    async payPackage() {
        this.isLoading = true;
        const base = window.location.origin;
        const successUrl = `${base}/organisations/${this.organisationId}/aboPayment/confirmation`;
        const cancelUrl = `${base}/`;

        const paymentData = {
            successUrl,
            cancelUrl,
            quantity: 1,
            priceId: this.package.priceId.id,
            orgId: this.organisationId,
        };

        try {
            const response = await this.stripeService.addAndPay(paymentData).toPromise();
            if (response?.id) {
                window.location.href = response.id;
            } else {
                this.isLoading = false;
                this.showErrorDialog();
            };
        } catch (error) {
            this.isLoading = false;
            this.showErrorDialog();
        };
    }

    getPackage() {
        const selectedPackage = this.localSelectionService.getSelection('selectedPackage-register');
        if (selectedPackage && typeof selectedPackage === "object") {
            this.package = selectedPackage;
        } else {
            // TO DO: Handle no package selected. Maybe redirect after orga was created.
            // this.dialogService.openDialog(
            //     'Kein Tarif ausgewählt',
            //     'Bitte wähle einen Tarif aus, um fortzufahren.'
            // ).subscribe(() => {
            //     this.router.navigate(['']);
            // });
        }
    }

    private showErrorDialog(): void {
        this.dialogService.openDialog(
            'Zahlung fehlgeschlagen',
            'Die Weiterleitung zum Zahlungsanbieter ist leider fehlgeschlagen. Bitte versuchen Sie es später noch einmal.'
        ).subscribe(() => {
            this.router.navigate(['organisations/' + this.organisationId + '/information']);
        });
    }

    checkPhonenumber(): boolean {
        const phone = this.phoneNumber.value?.trim();

        if (!phone) {
            return true;
        }

        const isValid = this.utilService.validateInternationalPhone(phone);

        if (!isValid) {
            this.phoneNumber.setErrors({ invalid: true });
            return false;
        }
        return true;
    }

    private setActiveProvider(org: OrganisationInterface): void {
        const image = org.fileId
            ? API_BASE_FILESERVICE +
            API_MAP['files']['GET'].replace(':fileId', org.fileId)
            : environment.defaultPictureOrganisationProducts;

        this.headerService.setActiveProvider({
            name: org.organisationName,
            img: image,
            organisationId: org.organisationId
        });
    }

    private setupContactToggle() {
        const createContactControl = this.organisationEditForm.get('createContact');
        const contactGroup = this.organisationEditForm.get('contact');

        this.createContactSub = createContactControl.valueChanges.subscribe((create) => {
            if (create) {
                contactGroup.get('contactName').setValidators([Validators.required]);
                contactGroup.get('email').setValidators([Validators.required, Validators.email]);
            } else {
                contactGroup.get('contactName').clearValidators();
                contactGroup.get('email').clearValidators();
                contactGroup.reset();
            }
            contactGroup.get('contactName').updateValueAndValidity();
            contactGroup.get('email').updateValueAndValidity();
        });
    }

    ngOnDestroy(): void {
        this.createContactSub?.unsubscribe();
    }

    trimField(controlName: string, group: 'contact' | null = null) {
        const control = group
            ? this.organisationEditForm.get(`${group}.${controlName}`)
            : this.organisationEditForm.get(controlName);

        if (control) {
            control.setValue(control.value?.trim());
        }
    }
}
